import { KTDatepickerStateManager } from "./config";
import { DateTimeDisplayInterface, DatePickerSegment, SelectedDateInterface } from "./types";

export const DefaultTemplates = {

	dropdownContainer: (_state: KTDatepickerStateManager) => _html`
		<div data-kt-datepicker-dropdown class="hidden absolute z-10 bg-white border border-gray-300 rounded shadow-md">
		</div>
	`,

	calendar: (state: KTDatepickerStateManager, visibleMonths = 1) => _html`
		<div data-kt-datepicker-calendar>
			<div>
				<button data-kt-datepicker-prev-btn aria-label="Previous month">&lt;</button>
				<button data-kt-datepicker-next-btn aria-label="Next month">&gt;</button>
			</div>
			<div class="${state.getConfig().containerClass}">
				${Array.from({ length: visibleMonths }).map(() => `
					<div data-kt-datepicker-month-container>
						<div data-kt-datepicker-month-year-display class="${state.getConfig().monthContainerClass}">
							<span data-kt-datepicker-month-display></span> <span data-kt-datepicker-year-display></span>
						</div>
						<div data-kt-datepicker-days-of-week></div>
						<div data-kt-datepicker-grid></div>
					</div>
				`).join('')}
			</div>
		</div>
	`,

	daysOfWeek: (_state: KTDatepickerStateManager, weekdays: string[]) => _html`
		<div data-kt-datepicker-days-of-week>
			${weekdays.map(day => `
				<span>${day}</span>
			`).join('')}
		</div>
	`,

	dayCell: (state: KTDatepickerStateManager, date: Date, isSelected: boolean, isToday: boolean, isDisabled: boolean, isInRange: boolean, isStartDate: boolean, isEndDate: boolean) => _html`
		<span data-kt-datepicker-cell class="${state.getConfig().dayCellClass}
				${isSelected ? state.getConfig().dayCellSelectedClass : ''}
				${isToday ? state.getConfig().dayCellCurrentClass : ''}
				${isDisabled ? state.getConfig().dayCellDisabledClass : ''}
				${isInRange ? state.getConfig().dayCellInRangeClass : ''}
				${isStartDate ? state.getConfig().dayCellStartClass : ''}
				${isEndDate ? state.getConfig().dayCellEndClass : ''}"
				data-day="${date.getDate()}"
				data-month="${String(date.getMonth())}"
				data-year="${date.getFullYear()}"
				${isDisabled ? 'data-disabled="true"' : ''}>
			${date.getDate()}
		</span>
	`,

	week: (_state: KTDatepickerStateManager, daysHtml: string[]) => _html`
		<div>${daysHtml.join('')}</div>
	`,

	monthYearSelector: (state: KTDatepickerStateManager,) => _html`
		<div data-kt-datepicker-month-year-selector class="${state.getConfig().monthYearSelectorContainerClass}">
			<div class="flex items-center justify-between mb-4">
				<div class="${state.getConfig().monthYearDisplayClass}" data-kt-datepicker-month-year-display>
					<span class="${state.getConfig().monthYearDisplayTextClass}" data-kt-datepicker-month-display></span>
					<span class="${state.getConfig().monthYearDisplayTextClass}" data-kt-datepicker-year-display></span>
				</div>
			</div>

			<div class="${state.getConfig().monthYearOptionsContainerClass}">
				<div class="overflow-auto flex-shrink-0 mr-4" data-kt-datepicker-month-options></div>
				<div class="overflow-auto flex-shrink-0" data-kt-datepicker-year-options></div>
			</div>
		</div>
	`,

	monthOptions: (state: KTDatepickerStateManager, monthNames: string[], selectedMonthIndex: number) => {
		return monthNames.map((month, monthIndex) => {
			const isCurrentMonth = monthIndex === new Date().getMonth();
			return `<button data-kt-datepicker-month-option class="${state.getConfig().monthOptionClass} ${monthIndex === selectedMonthIndex ? state.getConfig().monthOptionSelectedClass : ''} ${isCurrentMonth ? state.getConfig().monthOptionCurrentClass : ''}" data-month="${String(monthIndex)}">
				${month}
			</button>`;
		}).join('');
	},

	yearOptions: (state: KTDatepickerStateManager, selectedYear: number, yearLength: number = 10) => {
		const startYear = selectedYear - Math.floor(yearLength / 2);
		return Array.from({ length: yearLength }, (_, i) => startYear + i).map(year => {
			const isCurrentYear = year === new Date().getFullYear();
			return `<button data-kt-datepicker-year-option class="${state.getConfig().yearOptionClass} ${year === selectedYear ? state.getConfig().yearOptionSelectedClass : ''} ${isCurrentYear ? state.getConfig().yearOptionCurrentClass : ''}" data-year="${year}">
				${year}
			</button>`;
		}).join('');
	},

	datetimeDisplay: (state: KTDatepickerStateManager, datetime: DateTimeDisplayInterface, showDate = true, showTime = false, showGMT = false, is12Hours = true) => {
		const config = state.getConfig();

		return _html`
			<div class="${config.datetimeDisplayClass}" data-kt-datepicker-display>
				${showDate && `
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.Day}">${datetime.day}</span>
					/
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.Month}">${datetime.month}</span>
					/
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.Year}">${datetime.year}</span>
					${showTime ? '<span>&nbsp;</span>' : ''}
				`}
				${showTime && `
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.Hours}">${datetime.hours}</span>
					:
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.Minutes}">${datetime.minutes}</span>
					:
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.Seconds}">${datetime.seconds}</span>
					${is12Hours ? `
						<span>&nbsp;</span>
						<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.AmPm}">${datetime.amPm}</span>
					` : ''}
				`}
				${showGMT && `
					<span>${datetime.gmtOffset}</span>
				`}
			</div>
		`;
	},

	datetimeDisplayEnd: (state: KTDatepickerStateManager, datetime: DateTimeDisplayInterface, showDate = true, showTime = false, showGMT = false, is12Hours = true) => {
		const config = state.getConfig();

		return _html`
			<div class="${config.datetimeDisplayClass}" data-kt-datepicker-display>
				${showDate && `
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.EndDay}">${datetime.day}</span>
					/
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.EndMonth}">${datetime.month}</span>
					/
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.EndYear}">${datetime.year}</span>
					${showTime ? '<span>&nbsp;</span>' : ''}
				`}
				${showTime && `
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.EndHours}">${datetime.hours}</span>
					:
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.EndMinutes}">${datetime.minutes}</span>
					:
					<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.EndSeconds}">${datetime.seconds}</span>
					${is12Hours ? `
						<span>&nbsp;</span>
						<span class="${config.datetimeSegmentClass}" data-kt-datepicker-segment="${DatePickerSegment.EndAmPm}">${datetime.amPm}</span>
					` : ''}
				`}
				${showGMT && `
					<span>${datetime.gmtOffset}</span>
				`}
			</div>
		`;
	},

	datetimeDisplayRange: (state: KTDatepickerStateManager, startDatetime: DateTimeDisplayInterface, endDatetime: DateTimeDisplayInterface, showDate: boolean = true, showTime: boolean = false, showGMT: boolean = false, is12Hours: boolean = false) => _html`
		<div class="${state.getConfig().datetimeDisplayClass}" data-kt-datepicker-display>
			${DefaultTemplates.datetimeDisplay(state, startDatetime, showDate, showTime, showGMT, is12Hours)?.outerHTML}
			<span class="mx-2">-</span>
			${DefaultTemplates.datetimeDisplayEnd(state, endDatetime, showDate, showTime, showGMT, is12Hours)?.outerHTML}
		</div>
	`,

	timeSelectorDisplay: (state: KTDatepickerStateManager, datetime: DateTimeDisplayInterface, hourStep: number = 1, minuteStep: number = 1, secondStep: number = 1, disabledHours: number[] = [], disabledMinutes: number[] = [], is12Hours: boolean, selected: Date) => {

		const hours = Array.from({ length: is12Hours ? 12 / hourStep : 24 / hourStep }, (_, i) => (is12Hours ? 1 : 0) + i * hourStep)
			.map(hour => ({
				value: is12Hours ? (hour % 12) || 12 : hour,
				disabled: disabledHours.includes(hour),
				displayValue: hour.toString().padStart(2, '0')
			}));

		const minutes = Array.from({ length: 60 / minuteStep }, (_, i) => i * minuteStep)
			.map(minute => ({ value: minute, disabled: disabledMinutes.includes(minute) }));

		const seconds = Array.from({ length: 60 / secondStep }, (_, i) => i * secondStep)
			.map(second => ({ value: second }));

		return _html`
		  <div class="${state.getConfig().datetimeDisplayClass}">
			<div class="relative">
			  ${DefaultTemplates.datetimeDisplay(state, datetime, false, true, true)?.outerHTML}
			  <i class="fa fa-clock-o absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer"></i>
			</div>

			<div class="${state.getConfig().timeListContainerClass}" data-time-list-container>
			  <div class="${state.getConfig().timeSelectorFlexContainerClass}">
				<div class="${state.getConfig().timePartContainerClass}" data-time-part="hour">
				  <label for="hour-select" class="${state.getConfig().timePartLabelClass}">Hour</label>
				  <select id="hour-select" class="${state.getConfig().timeSelectClass}" data-kt-datepicker-segment="${DatePickerSegment.Hours}">
					${hours.map(hour => `
					  <option value="${hour.value}" ${hour.disabled ? 'disabled' : ''} ${selected && hour.value === (is12Hours ? (selected.getHours() % 12 || 12) : selected.getHours()) ? 'selected' : ''}>
						${hour.displayValue}
					  </option>
					`).join('')}
				  </select>
				</div>

				<div class="${state.getConfig().timePartContainerClass}" data-time-part="minute">
				  <label for="minute-select" class="${state.getConfig().timePartLabelClass}">Minute</label>
				  <select id="minute-select" class="${state.getConfig().timeSelectClass}" data-kt-datepicker-segment="${DatePickerSegment.Minutes}">
					${minutes.map(minute => `
					  <option value="${minute.value}" ${minute.disabled ? 'disabled' : ''} ${selected && minute.value === selected.getMinutes() ? 'selected' : ''}>
						${minute.value.toString().padStart(2, '0')}
					  </option>
					`).join('')}
				  </select>
				</div>

				<div class="${state.getConfig().timePartContainerClass}" data-time-part="second">
				  <label for="second-select" class="${state.getConfig().timePartLabelClass}">Second</label>
				  <select id="second-select" class="${state.getConfig().timeSelectClass}" data-kt-datepicker-segment="${DatePickerSegment.Seconds}">
					${seconds.map(second => `
					  <option value="${second.value}" ${selected && second.value === selected.getSeconds() ? 'selected' : ''}>
						${second.value.toString().padStart(2, '0')}
					  </option>
					`).join('')}
				  </select>
				</div>

				${is12Hours ? `
				  <div class="${state.getConfig().timePartContainerClass}" data-time-part="am-pm">
					<label for="am-pm-select" class="${state.getConfig().timePartLabelClass}">AM/PM</label>
					<select id="am-pm-select" class="${state.getConfig().timeSelectClass}" data-kt-datepicker-segment="${DatePickerSegment.AmPm}">
					  <option value="0" ${selected && selected.getHours() < 12 ? 'selected' : ''}>AM</option>
					  <option value="12" ${selected && selected.getHours() >= 12 ? 'selected' : ''}>PM</option>
					</select>
				  </div>
				` : ''}
			  </div>
			</div>
		  </div>
		`;
	},

	timeSelectorRangeDisplay: (state: KTDatepickerStateManager, startDateTime: DateTimeDisplayInterface, endDateTime: DateTimeDisplayInterface, hourStep: number = 1, minuteStep: number = 1, secondStep: number = 1, disabledHours: number[] = [], disabledMinutes: number[] = [], is24Hours: boolean, selected: SelectedDateInterface) => _html`
		<div class="flex items-center">
			<div data-kt-datepicker-time-type="start">
				${DefaultTemplates.timeSelectorDisplay(
		state, startDateTime, hourStep, minuteStep, secondStep, disabledHours, disabledMinutes, is24Hours, selected.selectedDateRange.startDate
	)?.outerHTML}
			</div>
			<span class="text-center w-4">-</span>
			<div data-kt-datepicker-time-type="end">
				${DefaultTemplates.timeSelectorDisplay(
		state, endDateTime, hourStep, minuteStep, secondStep, disabledHours, disabledMinutes, is24Hours, selected.selectedDateRange.endDate
	)?.outerHTML}
			</div>
		</div>
	`,
};

/**
 * Creates HTML elements from a template string.
 *
 * @param {TemplateStringsArray} strings - The template string array.
 * @param {any[]} values - The values to interpolate into the template string.
 * @return {HTMLElement} The created HTML element.
 */
export function _html(strings: TemplateStringsArray, ...values: any[]): HTMLElement {
	const template = document.createElement('template');
	template.innerHTML = strings.reduce((acc, str, i) => acc + (values[i - 1] || '') + str);
	return template.content.firstElementChild as HTMLElement;
}
